import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDynamicTranslationForArray } from '@monorepo/client-common';
import { Button, Stack, useTheme } from '@monorepo/nova-ui';
import { HeaderMenuLink } from '@monorepo/types';
import { useHeaderMenuLinks } from '../../../api/queries/use-header-menu-queries';
import { useMenuTranslationsQuery } from '../../../api/queries/use-menu-translations-query';
import { useConditionalLink } from '../../../hooks';

export const MenuDesktop = ({
  isSubMenuOpen,
  setIsSubMenuOpen,
  activeMenu,
  setActiveMenu,
  isTransparent,
}: {
  isSubMenuOpen: boolean;
  setIsSubMenuOpen: (isSubMenuOpen: boolean) => void;
  activeMenu: HeaderMenuLink | null;
  setActiveMenu: (activeMenu: HeaderMenuLink | null) => void;
  isTransparent: boolean;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { data: headerMenuLinks } = useHeaderMenuLinks();
  const { data: menuTranslations } = useMenuTranslationsQuery();
  const i18nEntries = useMemo(
    () => menuTranslations?.['headerMenuLinks'] || [],
    [menuTranslations],
  );
  const Link = useConditionalLink();
  const [hoveredLinkId, setHoveredLinkId] = useState<string | null>(null);

  const handleMenuClick = (id: string) => {
    const clickedMenu = headerMenuLinks?.find((link) => link.id === id) ?? null;

    if (activeMenu?.id === id) {
      setIsSubMenuOpen(!isSubMenuOpen);
      setActiveMenu(null);
    } else {
      setActiveMenu(clickedMenu);
      setIsSubMenuOpen(true);
    }
  };

  const handleMenuHover = (id: string) => {
    const hoveredMenu = headerMenuLinks?.find((link) => link.id === id) ?? null;

    setHoveredLinkId(hoveredMenu?.id ?? null);
  };

  return (
    <Stack
      sx={{
        height: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 3,
        alignSelf: 'stretch',
      }}
    >
      {!!headerMenuLinks?.length &&
        headerMenuLinks.map((item) => {
          const translatedLabel = getDynamicTranslationForArray(
            item.label,
            i18nEntries,
            item.id,
            'label',
            t,
          );

          return item.active ? (
            <Button
              key={item.id}
              className="menu-desktop-button"
              sx={{
                height: '100%',
                fontFamily: 'General Sans',
                fontSize: theme.typography.pxToRem(16),
                minWidth: {
                  xs: 'auto',
                  xl: theme.typography.pxToRem(148),
                },
                fontWeight:
                  activeMenu?.id === item.id || hoveredLinkId === item.id
                    ? 600
                    : 400,
                lineHeight: '150%',
                textTransform: 'none',
                padding: theme.spacing(4),
                color:
                  activeMenu?.id === item.id || hoveredLinkId === item.id
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
                backgroundColor: isTransparent
                  ? 'transparent'
                  : activeMenu?.id === item.id || hoveredLinkId === item.id
                    ? theme.palette.background.paper
                    : theme.palette.background.default,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
              onMouseOver={() => handleMenuHover(item.id)}
              onMouseOut={() => setHoveredLinkId(null)}
              onClick={() => handleMenuClick(item.id)}
            >
              {translatedLabel}
            </Button>
          ) : (
            <Link
              key={item.id}
              href={item.href ?? ''}
              nextjs={item.nextjs}
              {...(item.openInNewTab && {
                target: '_blank',
                rel: 'noopener noreferrer',
              })}
              className="menu-desktop-button"
              onMouseOver={() => {
                handleMenuHover(item.id);
              }}
              onMouseOut={() => setHoveredLinkId(null)}
              onClick={() => {
                setIsSubMenuOpen(false);
                setActiveMenu(null);
              }}
              style={{
                height: '100%',
                fontFamily: 'General Sans',
                fontSize: theme.typography.pxToRem(16),
                minWidth: 'auto',
                fontWeight:
                  activeMenu?.id === item.id || hoveredLinkId === item.id
                    ? 600
                    : 400,
                lineHeight: '150%',
                textTransform: 'none',
                padding: theme.spacing(4),
                color:
                  activeMenu?.id === item.id || hoveredLinkId === item.id
                    ? theme.palette.primary.main
                    : theme.palette.text.primary,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
              }}
            >
              {translatedLabel}
            </Link>
          );
        })}
    </Stack>
  );
};
